import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { UploadedChunkObject } from 'src/api/services/MediaClient';

const MAX_PARTS = 1000; // 1000 is the max number of parts allowed by S3

/**
 * Backoff between attempts at the same chunk. Without it, all four attempts at a
 * part were spent within milliseconds of each other, so a brief network blip or
 * a momentary S3 throttle consumed the whole retry budget and the part was lost.
 */
const RETRY_BACKOFF_MS = [1000, 3000, 8000];

/**
 * Sleep, but wake immediately if the upload is aborted. Without the signal, a
 * worker part-way through an 8s backoff keeps the whole upload alive for the
 * rest of that sleep after the user has already cancelled.
 */
const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }

    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    function onAbort() {
      clearTimeout(timer);
      resolve();
    }

    signal?.addEventListener('abort', onAbort, { once: true });
  });

/**
 * An upload that did not deliver every part.
 *
 * This exists because the alternative is worse than a failed upload: S3
 * validates the ETags of the parts you *name* in CompleteMultipartUpload but
 * does not check that you named all of them, so completing with a subset
 * produces a valid, permanently truncated object and a cheerful 200. The
 * customer is told "Upload complete", the request goes to an editor, and the
 * file turns out to be unplayable days later.
 */
class IncompleteUploadError extends Error {
  constructor(
    public readonly missingParts: number[],
    public readonly totalParts: number,
    public readonly reason?: unknown,
  ) {
    const detail = (reason as Error)?.message;
    super(
      `Upload incomplete: ${missingParts.length} of ${totalParts} part(s) did not upload` +
        (detail ? ` (${detail})` : ''),
    );
    this.name = 'IncompleteUploadError';
  }
}

/**
 * Calculate optimal part size for S3 multipart upload
 * Uses larger chunks for larger files to reduce overhead
 *
 * @param fileSize - Size of file in bytes
 * @returns Optimal part size in bytes
 */
const calculatePartSize = (fileSize: number): number => {
  const MB = 1024 * 1024;

  // Calculate minimum part size to stay within S3's 1000 part limit
  const minPartSizeForLimit = Math.ceil(fileSize / MAX_PARTS);

  // For files < 50MB: use 6MB chunks
  if (fileSize < 50 * MB) {
    return Math.max(minPartSizeForLimit, 6 * MB);
  }

  // For files 50MB - 500MB: use 10MB chunks
  if (fileSize < 500 * MB) {
    return Math.max(minPartSizeForLimit, 10 * MB);
  }

  // For files 500MB - 2GB: use 20MB chunks
  if (fileSize < 2000 * MB) {
    return Math.max(minPartSizeForLimit, 20 * MB);
  }

  // For files > 2GB: use 50MB chunks
  return Math.max(minPartSizeForLimit, 50 * MB);
};

const calculateChunks = (fileSize: number): number => {
  const partSize = calculatePartSize(fileSize);
  return Math.min(Math.ceil(fileSize / partSize), MAX_PARTS);
};

class S3Uploader {
  public readonly concurrentUploads = 6; // Concurrent uploads limit
  public readonly maxUploadAttempts = 3; // Max upload retries per chunk

  private axios: AxiosInstance;
  private abortSignal?: AbortSignal;
  private uploadQueue: number[]; // Part numbers queue
  private uploadedParts: UploadedChunkObject[];
  private uploadPartAttempts: { [key: number]: number }; // Number of upload attempts per chunk
  private firstError: unknown;
  private cancelError: unknown;

  constructor(controller?: AbortController) {
    this.axios = axios.create({
      signal: controller?.signal,
    });
    this.abortSignal = controller?.signal;

    this.uploadQueue = [];
    this.uploadedParts = [];
    this.uploadPartAttempts = {};
    this.firstError = undefined;
    this.cancelError = undefined;
  }

  getChunkBlob = (file: File, start: number, end: number): Blob => file.slice(start, end);

  async upload(
    signedUrls: string[],
    file: File,
    onUploadProgress: (event: any, chunkIndex: number) => void,
    onFail?: (error: unknown) => void,
  ): Promise<UploadedChunkObject[]> {
    const fileSize = file.size;
    const numParts = signedUrls.length;
    const partSize = calculatePartSize(fileSize);

    // Initialize upload state for this upload
    this.uploadQueue = Array.from({ length: numParts }, (_, i) => i);
    this.uploadedParts = new Array(numParts);
    this.uploadPartAttempts = {};
    this.firstError = undefined;
    this.cancelError = undefined;

    // A fixed pool of workers, each draining the shared queue. This replaces a
    // 100ms setInterval that watched for "no active uploads and an empty queue":
    // a chunk that failed terminally decremented the active count without
    // re-driving the queue, so with parts still queued that condition could
    // never be met — the promise never settled, the interval leaked, and the
    // card sat on "Uploading" forever.
    const workers = Array.from({ length: Math.min(this.concurrentUploads, numParts) }, () =>
      this.drainQueue(signedUrls, file, partSize, onUploadProgress),
    );

    await Promise.all(workers);

    this.uploadPartAttempts = {};

    // Never hand a partial part list to CompleteMultipartUpload. The previous
    // `.filter(part => part !== undefined)` here is what turned a failed chunk
    // into a silently truncated file that reported success.
    const missingParts: number[] = [];
    for (let part = 0; part < numParts; part++) {
      if (!this.uploadedParts[part]) {
        missingParts.push(part + 1);
      }
    }

    if (missingParts.length > 0 || this.firstError || this.cancelError) {
      // A cancellation is the user's own doing, and callers already treat it
      // differently from a broken upload, so let it through as itself rather
      // than relabelling it as an incomplete upload. Checked ahead of any
      // other error, and against the signal too, so an abort is reported as an
      // abort however it interleaved with a failing part.
      const cancelled = this.cancelError ?? (this.abortSignal?.aborted ? new axios.Cancel() : null);

      if (cancelled) {
        onFail?.(cancelled);
        throw cancelled;
      }

      const error = new IncompleteUploadError(missingParts, numParts, this.firstError);
      onFail?.(error);
      throw error;
    }

    return this.uploadedParts;
  }

  private async drainQueue(
    signedUrls: string[],
    file: File,
    partSize: number,
    onUploadProgress: (event: any, chunkIndex: number) => void,
  ): Promise<void> {
    for (;;) {
      const part = this.uploadQueue.shift();

      if (part === undefined) {
        return;
      }

      const attempt = (this.uploadPartAttempts[part] ?? 0) + 1;
      this.uploadPartAttempts[part] = attempt;

      try {
        await this.uploadChunk(signedUrls, file, part, partSize, onUploadProgress);
      } catch (error: any) {
        if (axios.isCancel(error)) {
          // Kept apart from firstError: a cancel that arrives *after* some part
          // had already given up would otherwise be reported as an incomplete
          // upload, because firstError only remembers the first thing to go
          // wrong. What the user did is the more useful answer.
          this.cancelError ??= error;
          // An abort is deliberate and applies to the whole upload — the shared
          // axios signal has already killed every other request in flight.
          this.uploadQueue.length = 0;
          return;
        }

        if (attempt <= this.maxUploadAttempts) {
          await wait(
            RETRY_BACKOFF_MS[Math.min(attempt - 1, RETRY_BACKOFF_MS.length - 1)],
            this.abortSignal,
          );
          this.uploadQueue.push(part);
          continue;
        }

        // Out of attempts. Remember why and keep draining, so the error can
        // name every part that is missing rather than only the first one to
        // give up.
        //
        // Not a fast-fail, deliberately. A retry re-queues to the TAIL, so a
        // part cannot exhaust four attempts until it has been drawn four times
        // — which on a long queue is near the end of the upload regardless.
        // Bailing here would therefore save little (measured: 63 PUTs for a
        // 60-part upload whose first part never succeeds) while giving up the
        // thing tail re-queueing buys, which is a retry window minutes wide
        // instead of seconds. On multi-GB footage over a flaky link that window
        // is what turns a transient outage into a completed upload.
        this.recordError(error);
      }
    }
  }

  private recordError(error: unknown): void {
    if (this.firstError === undefined) {
      this.firstError = error;
    }
  }

  private async uploadChunk(
    signedUrls: string[],
    file: File,
    part: number,
    partSize: number,
    onUploadProgress: (event: any, chunkIndex: number) => void,
  ) {
    const signedUrl = signedUrls[part];
    const start = part * partSize;
    const end = Math.min((part + 1) * partSize, file.size);

    const options: AxiosRequestConfig = {
      onUploadProgress: (event: any) => onUploadProgress(event, part),
    };

    const blob = this.getChunkBlob(file, start, end);
    const response = await this.axios.put(signedUrl, blob, options);
    const etag = response.headers.etag;

    // S3 rejects a part list with a missing ETag, but only at completion time
    // and with an opaque validation error. The usual cause is the bucket's CORS
    // config dropping ETag from ExposeHeaders, which is worth naming here rather
    // than debugging from a 422 later.
    if (!etag) {
      throw new Error(
        `S3 did not return an ETag for part ${part + 1} — check the bucket's CORS ExposeHeaders`,
      );
    }

    this.uploadedParts[part] = {
      ETag: etag,
      PartNumber: part + 1,
    };
  }
}

export { S3Uploader, IncompleteUploadError, calculateChunks, calculatePartSize };
